import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { localizeCategory } from '../../utils/format';
import { apiClient } from '../../api/client';
import { PaymentMethod } from '../../types';
import './QuickTransactionFlow.css';

interface FrequentCategory {
  category: string;
  icon: string;
  count: number;
  avg_amount: number;
  frequent_titles: string[];
}

interface QuickTransactionFlowProps {
  category: FrequentCategory;
  onClose: () => void;
  onSave: (title: string, amount: number, category: string, payment_method_id?: string) => Promise<void>;
}

const QuickTransactionFlow: React.FC<QuickTransactionFlowProps> = ({
  category,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [title, setTitle] = useState('');
  const [showTitleSuggestions, setShowTitleSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | undefined>(undefined);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);

  // Блокируем скролл body когда модалка открыта
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      // Восстанавливаем скролл при закрытии
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  // Загружаем счета при открытии
  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
      const response = await apiClient.getPaymentMethods();
      const methods = response.payment_methods || [];
      
      // Сортируем: счет по умолчанию первым
      const sortedMethods = [...methods].sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return 0;
      });
      
      setPaymentMethods(sortedMethods);
      
      // Автоматически выбираем счет по умолчанию
      if (sortedMethods.length > 0) {
        const defaultMethod = sortedMethods.find(m => m.is_default);
        if (defaultMethod) {
          setSelectedPaymentMethodId(defaultMethod.id);
        } else {
          // Если нет счета по умолчанию, выбираем первый
          setSelectedPaymentMethodId(sortedMethods[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.,]/g, '');
    setAmount(value);
  };

  const handleTitleFocus = () => {
    setShowTitleSuggestions(true);
  };

  const handleTitleSelect = (selectedTitle: string) => {
    setTitle(selectedTitle);
    setShowTitleSuggestions(false);
  };

  const handleSubmit = async () => {
    const numAmount = parseFloat(amount.replace(',', '.'));
    if (!numAmount || numAmount <= 0) {
      return;
    }

    const finalTitle = title.trim() || category.category;

    setIsSubmitting(true);
    try {
      await onSave(finalTitle, numAmount, category.category, selectedPaymentMethodId);
      onClose();
    } catch (error) {
      console.error('Failed to save transaction:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && amount && !isSubmitting) {
      handleSubmit();
    }
  };

  return createPortal(
    <div className="quick-transaction-flow">
      <div className="quick-transaction-flow__backdrop" onClick={onClose} />
      <div className="quick-transaction-flow__modal">
        <div className="quick-transaction-flow__header">
          <div className="quick-transaction-flow__category">
            <span className="quick-transaction-flow__icon">{category.icon}</span>
            <h2 className="quick-transaction-flow__title">{localizeCategory(category.category)}</h2>
          </div>
          <button className="quick-transaction-flow__close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="quick-transaction-flow__body">
          {/* Сумма */}
          <div className="quick-transaction-flow__field">
            <label className="quick-transaction-flow__label">
              {t('addTransaction.amount')} *
            </label>
            <input
              type="text"
              inputMode="decimal"
              className="quick-transaction-flow__input quick-transaction-flow__input--amount"
              placeholder={t('addTransaction.amount')}
              value={amount}
              onChange={handleAmountChange}
              onKeyPress={handleKeyPress}
            />
            {category.avg_amount > 0 && (
              <div className="quick-transaction-flow__hint">
                {t('quickTransaction.avgAmount')}: {category.avg_amount.toFixed(0)}
              </div>
            )}
          </div>

          {/* Название с подсказками */}
          <div className="quick-transaction-flow__field">
            <label className="quick-transaction-flow__label">
              {t('addTransaction.title')}
            </label>
            <input
              type="text"
              className="quick-transaction-flow__input"
              placeholder={t('quickTransaction.selectOrEnter')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onFocus={handleTitleFocus}
              onKeyPress={handleKeyPress}
            />
            
            {showTitleSuggestions && category.frequent_titles.length > 0 && (
              <div className="quick-transaction-flow__suggestions">
                <div className="quick-transaction-flow__suggestions-title">
                  {t('quickTransaction.popular')}:
                </div>
                <div className="quick-transaction-flow__suggestions-list">
                  {category.frequent_titles.map((suggestedTitle, index) => (
                    <button
                      key={index}
                      type="button"
                      className="quick-transaction-flow__suggestion"
                      onClick={() => handleTitleSelect(suggestedTitle)}
                    >
                      {suggestedTitle}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Выбор счета */}
          {paymentMethods.length > 0 && (
            <div className="quick-transaction-flow__field">
              <label className="quick-transaction-flow__label">
                {t('addTransaction.paymentMethod')}
              </label>
              <select
                className="quick-transaction-flow__input"
                value={selectedPaymentMethodId || ''}
                onChange={(e) => setSelectedPaymentMethodId(e.target.value || undefined)}
                disabled={isSubmitting || loadingPaymentMethods}
              >
                {loadingPaymentMethods ? (
                  <option value="">Загрузка счетов...</option>
                ) : (
                  paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.name}{method.is_default ? ' (по умолчанию)' : ''}
                    </option>
                  ))
                )}
              </select>
            </div>
          )}
        </div>

        <div className="quick-transaction-flow__footer">
          <button
            className="quick-transaction-flow__button quick-transaction-flow__button--secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </button>
          <button
            className="quick-transaction-flow__button quick-transaction-flow__button--primary"
            onClick={handleSubmit}
            disabled={!amount || isSubmitting}
          >
            {isSubmitting ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuickTransactionFlow;
